import { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Table } from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { useInstance } from 'app/libs/use-instance';
import { CanAccess } from 'app/railways/domains/access/features/permissions';
import { Loading } from 'app/ui';

import { TNewTariff, TTariff } from '../../core/tariff';
import { TTariffGrid } from '../../core/tariff-grid';
import { getTariffDetailsRequest } from '../../requests/get-tariff-details.request';
import { updateTariffDetailsRequest } from '../../requests/update-tariff-details.request';
import {
  ChangedCellsProvider,
  createChangeCellState,
} from './changed-cells-provider';
import {
  createTableGridData,
  TariffTableGridData,
} from './create-table-grid-data';
import { EditCellProvider } from './edit-cell-provider';
import { GridCellComponent } from './grid-cell-component';

export const TariffPricesGrid = observer(() => {
  const [gridData, setGridData] = useState<TariffTableGridData | null>(null);

  const changeCellState = useInstance(() => createChangeCellState());

  const notify = usePageNotify();

  const fetchTariffResource = useResource<void, TTariffGrid>({
    request: getTariffDetailsRequest,
    errorNotify: notify,
    onDone: (data) => {
      const result = createTableGridData(data);

      setGridData(result);
    },
  });

  const updateTariffResource = useResource<TNewTariff[], { result: number }>({
    request: updateTariffDetailsRequest,
    errorNotify: notify,
    onDone: (data) => {
      if (data.result !== 0) return;

      changeCellState.clear();
      notify.clearAll();
      notify.addMessage({
        type: 'success',
        message: 'Изменения сохранены',
      });
    },
  });

  useEffect(() => {
    fetchTariffResource.send();

    return gridData ? () => gridData.dataMap.clear() : () => {};
  }, []);

  const handleTariffChange = (tariff: TNewTariff | TTariff) => {
    const ids = {
      tariffTypeId: tariff.tariffType.id,
      trainTypeId: tariff.trainType.id,
      zoneId: tariff.zone.id,
    };
    gridData?.dataMap.setRecord(ids, { ...tariff, isEmpty: false });

    changeCellState.setChangeCellRecord(ids);
    if (changeCellState.getSize() > 0) {
      const messageId = 1;
      if (notify.hasMessage({ id: messageId })) return;

      notify.addMessage({
        id: messageId,
        type: 'warning',
        message: 'Есть изменения, которые не были отправлены на сервер',
      });
    }
  };

  const saveTariffChanges = () => {
    const changedTariffs = Array.from(gridData?.dataMap.values() ?? []).filter(
      (value) => !value.isEmpty,
    );

    updateTariffResource.send(changedTariffs);
  };

  if (fetchTariffResource.isLoading)
    return (
      <Card size="small">
        <Loading active />
      </Card>
    );

  if (!gridData) {
    return null;
  }

  return (
    <Loading active={updateTariffResource.isLoading}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card size="small">
            <ChangedCellsProvider state={changeCellState}>
              <EditCellProvider
                cellDataMap={gridData.dataMap}
                onChange={handleTariffChange}
              >
                <Table
                  columns={gridData.cols}
                  components={{
                    body: {
                      cell: GridCellComponent,
                    },
                  }}
                  dataSource={gridData.rows}
                  size="small"
                  pagination={false}
                  tableLayout="auto"
                  bordered
                />
              </EditCellProvider>
            </ChangedCellsProvider>
          </Card>
        </Col>

        <CanAccess perm="PERM_TARIFF_UPDATE">
          <Col span={24}>
            <Card
              size="small"
              style={{ display: 'flex', justifyContent: 'end' }}
            >
              <Button type="primary" onClick={saveTariffChanges}>
                Сохранить
              </Button>
            </Card>
          </Col>
        </CanAccess>
      </Row>
    </Loading>
  );
});
